import { authClient } from "./auth-client";
import { apiUrl } from "./api";

export const CONTACT_TABLE_COLUMN_ORDER_SCOPE = "contact-table.column-order";
export const CONTACT_TABLE_COLUMN_VISIBILITY_SCOPE = "contact-table.column-visibility";
export const CONTACT_TABLE_COLUMN_WIDTHS_SCOPE = "contact-table.column-widths";

/**
 * „CO intern" zeigt dieselbe Tabelle wie die Kontaktliste, braucht aber EIGENE
 * Scopes.
 *
 * Nicht aus Ordnungsliebe: Die Variante hat eine Spalte mehr („CO-Art"), und
 * `isValidOrder` in ContactTableRedesign verwirft eine gespeicherte
 * Reihenfolge, deren Länge nicht passt. Mit gemeinsamen Scopes setzte jeder
 * Wechsel zwischen den beiden Listen die Spaltenreihenfolge der jeweils
 * anderen zurück — und eine verschobene Spaltenbreite hier verschöbe sie dort.
 */
export const COINTERN_TABLE_COLUMN_ORDER_SCOPE = "cointern-table.column-order";
export const COINTERN_TABLE_COLUMN_VISIBILITY_SCOPE = "cointern-table.column-visibility";
export const COINTERN_TABLE_COLUMN_WIDTHS_SCOPE = "cointern-table.column-widths";

/**
 * Tabellenansicht der Vorgangsliste als EIN versioniertes Paket (Reihenfolge,
 * Sichtbarkeit, Breiten, eingeklappte Spalten) — siehe utils/vorgaengeTableView.
 * Löst die früheren Einzel-Scopes `vorgaenge-table.column-order/-visibility/
 * -widths` ab: die wurden bei jeder Änderung automatisch geschrieben, während die
 * Ansicht jetzt erst auf ausdrückliches „Als Standard speichern" persistiert wird.
 * Die alten Scope-Zeilen bleiben in der DB verwaist zurück (reines Key/Value,
 * additive Ablösung) und werden nicht mehr gelesen.
 */
export const VORGAENGE_TABLE_VIEW_SCOPE = "vorgaenge-table.view";

/**
 * Spaltenansicht der Schaden-Übersicht (/damages) — dieselben drei Einzel-Scopes
 * wie bei den Kontakten: die Ansicht wird dort bei jeder Änderung automatisch
 * gespeichert, nicht erst auf ausdrückliches „Als Standard speichern".
 */
export const DAMAGE_TABLE_COLUMN_ORDER_SCOPE = "damage-table.column-order";
export const DAMAGE_TABLE_COLUMN_VISIBILITY_SCOPE = "damage-table.column-visibility";
export const DAMAGE_TABLE_COLUMN_WIDTHS_SCOPE = "damage-table.column-widths";

/**
 * Stable per-user key used to scope frontend prefs (e.g. column orders).
 * Derived from the better-auth session — previously came from MSAL active
 * account, which no longer exists in the browser.
 *
 * H1: Der Key wird vom AuthContext (hält die Session ohnehin reaktiv via
 * useSession) in diesen Modul-Cache gespiegelt. getCurrentUserPreferenceKey
 * bevorzugt den Cache und macht damit KEIN eigenes authClient.getSession() mehr —
 * der Roundtrip pro Preference-Ladung entfällt. Fallback bleibt getSession, falls
 * der Cache (sehr früh, vor dem AuthContext) noch leer ist.
 */
let cachedUserPreferenceKey: string | null = null;

/** Vom AuthContext gesetzt, sobald sich die Session ändert (H1). */
export const setCurrentUserPreferenceKey = (key: string | null): void => {
  cachedUserPreferenceKey = key;
};

export const getCurrentUserPreferenceKey = async (): Promise<string | null> => {
  if (cachedUserPreferenceKey) return cachedUserPreferenceKey;
  try {
    const result = await authClient.getSession();
    const data: any = (result as any)?.data ?? result;
    const user = data?.user;
    if (!user) return null;

    const username = String(user.email ?? user.username ?? user.id ?? "").trim().toLowerCase();
    const tenantId = String(data?.session?.activeOrganizationId ?? "").trim().toLowerCase();
    if (!username) return null;
    return tenantId ? `${tenantId}:${username}` : username;
  } catch {
    return null;
  }
};

export const fetchUserPreference = async <T>(
  userKey: string,
  scope: string,
): Promise<T | null> => {
  const response = await fetch(
    apiUrl("user-preference", {
      userKey,
      scope,
    }),
  );

  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to load user preference: ${response.status}`);
  }

  const data = await response.json();
  return (data?.value ?? null) as T | null;
};

/**
 * Lädt MEHRERE Scopes in EINEM Request (Batch, H2) — statt N Einzel-GETs.
 * `userKey` ist server-kontrolliert (aus dem Bearer-Token), daher hier nicht nötig.
 * Rückgabe: Map scope → value; fehlende Scopes fehlen (⇒ Default beim Aufrufer).
 */
export const fetchUserPreferences = async (
  scopes: string[],
): Promise<Record<string, unknown>> => {
  if (!scopes.length) return {};
  const response = await fetch(apiUrl("user-preference/batch", { scopes: scopes.join(",") }));
  if (response.status === 404) return {};
  if (!response.ok) {
    throw new Error(`Failed to load user preferences: ${response.status}`);
  }
  const data = await response.json();
  return data && typeof data === "object" ? (data as Record<string, unknown>) : {};
};

export const saveUserPreference = async (
  userKey: string,
  scope: string,
  value: unknown,
): Promise<void> => {
  const response = await fetch(apiUrl("user-preference"), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userKey, scope, value }),
  });

  if (!response.ok) {
    throw new Error(`Failed to save user preference: ${response.status}`);
  }
};
